import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, Loader2, ArrowLeft, Sparkles, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Bills, OCR, fileToBase64 } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const emptyForm = {
  company: "",
  invoice_number: "",
  amount_due: "",
  due_date: "",
  notes: "",
  email: "",
  phone: "",
  cc_emails: "",
  bank_name: "",
  bsb: "",
  account_number: "",
  account_name: "",
  payment_reference: "",
  bill_type: "single", // single | recurring | plan
  frequency: "monthly", // weekly | fortnightly | monthly | quarterly | yearly
  occurrences: 12,
};

const Field = ({ label, children, testid }) => (
  <label data-testid={testid} className="block">
    <div className="swiss-label mb-1">{label}</div>
    {children}
  </label>
);

const CaptureBill = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const [form, setForm] = useState(emptyForm);
  const [imageBase64, setImageBase64] = useState(null);
  const [imageMime, setImageMime] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ocring, setOcring] = useState(false);
  const [ocringPayments, setOcringPayments] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [duplicate, setDuplicate] = useState(null);

  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (k === "invoice_number") setDuplicate(null);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const isImage = /^image\//.test(file.type);
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Please use an image (JPG/PNG/WEBP) or PDF.");
      return;
    }
    try {
      const { base64, mime } = await fileToBase64(file);
      setImageBase64(base64);
      setImageMime(mime);
      setImagePreview(isImage ? `data:${mime};base64,${base64}` : null);
      await runOCR(base64, mime);
    } catch {
      toast.error("Could not read the file.");
    }
  };

  const runOCR = async (base64, mime) => {
    setOcring(true);
    try {
      const res = await OCR.extract({ image_base64: base64, image_mime: mime });
      const next = { ...form };
      let filled = 0;
      if (res.company) { next.company = res.company; filled++; }
      if (res.invoice_number) { next.invoice_number = res.invoice_number; filled++; }
      if (res.amount_due != null) { next.amount_due = String(res.amount_due); filled++; }
      if (res.due_date) { next.due_date = res.due_date; filled++; }
      if (res.notes) { next.notes = res.notes; filled++; }
      if (res.email) { next.email = res.email; filled++; }
      if (res.phone) { next.phone = res.phone; filled++; }
      if (res.bank_name) { next.bank_name = res.bank_name; filled++; }
      if (res.bsb) { next.bsb = res.bsb; filled++; }
      if (res.account_number) { next.account_number = res.account_number; filled++; }
      if (res.account_name) { next.account_name = res.account_name; filled++; }
      if (res.payment_reference) { next.payment_reference = res.payment_reference; filled++; }
      if (res.bpay_biller_code) { next.bpay_biller_code = res.bpay_biller_code; filled++; }
      if (res.bpay_reference) { next.bpay_reference = res.bpay_reference; filled++; }
      if (res.payid) { next.payid = res.payid; filled++; }
      if (res.payment_url) { next.payment_url = res.payment_url; filled++; }
      if (res.accepted_methods) { next.accepted_methods = res.accepted_methods; filled++; }
      setForm(next);
      toast.success(
        filled > 0
          ? `AI pre-filled ${filled} field${filled === 1 ? "" : "s"}. Review before saving.`
          : "AI couldn't confidently read fields. Please enter manually."
      );
    } catch (e) {
      toast.error("OCR failed. You can still enter the details manually.");
    } finally {
      setOcring(false);
    }
  };

  const runPaymentOCR = async () => {
    if (!imageBase64) {
      toast.error("Upload or snap the bill first.");
      return;
    }
    setOcringPayments(true);
    try {
      const res = await OCR.paymentDetails({ image_base64: imageBase64, image_mime: imageMime });
      setForm((prev) => {
        const next = { ...prev };
        const fields = [
          "bank_name",
          "bsb",
          "account_number",
          "account_name",
          "payment_reference",
          "bpay_biller_code",
          "bpay_reference",
          "payid",
          "payment_url",
          "accepted_methods",
        ];
        let filled = 0;
        for (const f of fields) {
          const v = res[f];
          if (v != null && String(v).trim() !== "" && !String(prev[f] || "").trim()) {
            next[f] = String(v);
            filled++;
          }
        }
        setTimeout(() => {
          toast.success(
            filled > 0
              ? `Re-scan found ${filled} new payment field${filled === 1 ? "" : "s"}.`
              : "Re-scan found nothing new. You can enter it manually.",
          );
        }, 0);
        return next;
      });
    } catch {
      toast.error("Payment re-scan failed.");
    } finally {
      setOcringPayments(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  };

  const onSave = async (e) => {
    e?.preventDefault();
    setDuplicate(null);
    // Basic validation
    if (!form.company.trim()) return toast.error("Company name is required");
    if (!form.invoice_number.trim()) return toast.error("Invoice number is required");
    const amount = Number(form.amount_due);
    if (!amount || amount <= 0) return toast.error("Amount due must be greater than zero");
    if (!form.due_date) return toast.error("Due date is required");

    setSaving(true);
    try {
      const normalizeUrl = (u) => {
        if (!u) return "";
        const s = String(u).trim();
        if (!s) return "";
        if (/^https?:\/\//i.test(s)) return s;
        return `https://${s}`;
      };

      const payload = {
        company: form.company.trim(),
        invoice_number: form.invoice_number.trim(),
        amount_due: amount,
        due_date: form.due_date,
        notes: form.notes || "",
        email: form.email || "",
        phone: form.phone || "",
        cc_emails: form.cc_emails || "",
        bank_name: form.bank_name || "",
        bsb: form.bsb || "",
        account_number: form.account_number || "",
        account_name: form.account_name || "",
        payment_reference: form.payment_reference || "",
        bpay_biller_code: form.bpay_biller_code || "",
        bpay_reference: form.bpay_reference || "",
        payid: form.payid || "",
        payment_url: normalizeUrl(form.payment_url),
        accepted_methods: form.accepted_methods || "",
        image_base64: imageBase64,
        image_mime: imageMime,
        bill_type: form.bill_type,
        frequency: form.bill_type === "single" ? null : form.frequency,
        occurrences: form.bill_type === "single" ? 1 : Number(form.occurrences) || 1,
      };
      const created = await Bills.create(payload);
      if (created?.bills && Array.isArray(created.bills)) {
        toast.success(
          `${created.bills.length} ${form.bill_type === "plan" ? "installments" : "bills"} created.`,
        );
        navigate(`/bills/${created.first_bill_id}`);
      } else {
        toast.success("Bill saved to the cloud.");
        navigate(`/bills/${created.id}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === "duplicate_invoice") {
        setDuplicate(detail);
        toast.error("Duplicate invoice number. Save blocked.");
      } else {
        toast.error(detail?.message || "Could not save. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="capture-bill" className="fade-rise">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="swiss-label mb-3">Capture</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
        New Bill
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Take a photo or upload the invoice. We'll read it with AI and pre-fill the
        form. You review, correct anything, then save.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 mt-10">
        {/* Capture side */}
        <div>
          <div
            data-testid="dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`dropzone ${dragging ? "dragging" : ""}`}
          >
            {imagePreview ? (
              <div className="flex flex-col items-center gap-4">
                <img
                  data-testid="bill-preview"
                  src={imagePreview}
                  alt="Bill preview"
                  className="max-h-[420px] w-auto border border-neutral-200"
                />
                <div className="flex flex-wrap gap-3 justify-center">
                  <button
                    data-testid="reupload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-black hover:text-white transition-colors"
                  >
                    Replace Image
                  </button>
                  <button
                    data-testid="rerun-ocr-btn"
                    disabled={ocring}
                    onClick={() => runOCR(imageBase64, imageMime)}
                    className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 disabled:opacity-50 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                  >
                    {ocring ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    Re-run AI Read
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-6">
                <div className="h-16 w-16 border-2 border-black flex items-center justify-center">
                  <Camera className="h-7 w-7" strokeWidth={2} />
                </div>
                <div className="text-center">
                  <div className="font-display font-extrabold text-2xl">
                    Snap or drop a bill
                  </div>
                  <div className="text-neutral-500 text-sm mt-1">
                    JPG, PNG, or WEBP. AI extracts the details.
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    data-testid="take-photo-btn"
                    onClick={() => cameraInputRef.current?.click()}
                    className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2"
                  >
                    <Camera className="h-4 w-4" /> Take Photo
                  </button>
                  <button
                    data-testid="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-xs px-5 py-3 hover:bg-black hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" /> Upload Bill
                  </button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              data-testid="file-input"
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <input
              ref={cameraInputRef}
              data-testid="camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </div>

          {ocring && (
            <div
              data-testid="ocr-loading"
              className="mt-4 inline-flex items-center gap-2 text-sm text-neutral-600"
            >
              <Loader2 className="h-4 w-4 animate-spin" /> AI is reading your
              bill…
            </div>
          )}
        </div>

        {/* Form side */}
        <form data-testid="bill-form" onSubmit={onSave} className="space-y-5">
          {duplicate && (
            <div
              data-testid="duplicate-warning"
              className="border-2 border-red-500 bg-red-50 text-red-800 p-4 flex items-start gap-3"
            >
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-bold uppercase tracking-widest text-xs">
                  Duplicate Invoice Blocked
                </div>
                <div className="text-sm mt-1">
                  Invoice number already exists for{" "}
                  <span className="font-bold">
                    {duplicate.existing_company || "another bill"}
                  </span>
                  .{" "}
                  <button
                    type="button"
                    className="underline font-bold"
                    onClick={() =>
                      navigate(`/bills/${duplicate.existing_bill_id}`)
                    }
                  >
                    View existing
                  </button>{" "}
                  or change this invoice number.
                </div>
              </div>
            </div>
          )}

          <Field label="Company / Vendor" testid="field-company">
            <Input
              data-testid="input-company"
              className="swiss-input"
              value={form.company}
              onChange={update("company")}
              placeholder="Acme Supply Co."
            />
          </Field>
          <Field label="Invoice Number" testid="field-invoice">
            <Input
              data-testid="input-invoice"
              className="swiss-input hash"
              value={form.invoice_number}
              onChange={update("invoice_number")}
              placeholder="INV-00123"
            />
          </Field>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Amount Due" testid="field-amount">
              <Input
                data-testid="input-amount"
                className="swiss-input mono"
                value={form.amount_due}
                onChange={update("amount_due")}
                placeholder="0.00"
                inputMode="decimal"
              />
            </Field>
            <Field label="Due Date" testid="field-due-date">
              <Input
                data-testid="input-due-date"
                type="date"
                className="swiss-input"
                value={form.due_date}
                onChange={update("due_date")}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Contact Email" testid="field-email">
              <Input
                data-testid="input-email"
                className="swiss-input"
                value={form.email}
                onChange={update("email")}
                placeholder="billing@vendor.com"
                type="email"
              />
            </Field>
            <Field label="Contact Phone" testid="field-phone">
              <Input
                data-testid="input-phone"
                className="swiss-input"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+1 555 555 0100"
              />
            </Field>
          </div>
          <Field label="Email Cc (optional)" testid="field-cc-emails">
            <Input
              data-testid="input-cc-emails"
              className="swiss-input"
              value={form.cc_emails}
              onChange={update("cc_emails")}
              placeholder="bookkeeper@biz.com, accountant@biz.com"
            />
          </Field>
          <Field label="Notes" testid="field-notes">
            <Textarea
              data-testid="input-notes"
              className="swiss-input resize-none"
              value={form.notes}
              onChange={update("notes")}
              rows={3}
              placeholder="What is this bill for?"
            />
          </Field>

          <div className="pt-6">
            <div className="flex items-end justify-between gap-3 mb-4 border-b-2 border-black pb-2">
              <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-black">
                Bank / Payment Details
              </div>
              {imageBase64 && (
                <button
                  type="button"
                  data-testid="rescan-bank-btn"
                  onClick={runPaymentOCR}
                  disabled={ocringPayments || ocring}
                  className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-black hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {ocringPayments ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Re-scan
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Account Name" testid="field-account-name">
                <Input
                  data-testid="input-account-name"
                  className="swiss-input"
                  value={form.account_name}
                  onChange={update("account_name")}
                  placeholder="e.g. Acme Supply Pty Ltd"
                />
              </Field>
              <Field label="Bank Name" testid="field-bank-name">
                <Input
                  data-testid="input-bank-name"
                  className="swiss-input"
                  value={form.bank_name}
                  onChange={update("bank_name")}
                  placeholder="e.g. Commonwealth Bank"
                />
              </Field>
              <Field label="BSB" testid="field-bsb">
                <Input
                  data-testid="input-bsb"
                  className="swiss-input mono"
                  value={form.bsb}
                  onChange={update("bsb")}
                  placeholder="062-000"
                />
              </Field>
              <Field label="Account Number" testid="field-account-number">
                <Input
                  data-testid="input-account-number"
                  className="swiss-input mono"
                  value={form.account_number}
                  onChange={update("account_number")}
                  placeholder="12345678"
                />
              </Field>
              <Field label="Payment Reference" testid="field-payment-reference">
                <Input
                  data-testid="input-payment-reference"
                  className="swiss-input"
                  value={form.payment_reference}
                  onChange={update("payment_reference")}
                  placeholder="Usually the invoice number"
                />
              </Field>
            </div>
          </div>

          <div className="pt-6">
            <div className="flex items-end justify-between gap-3 mb-4 border-b-2 border-black pb-2">
              <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-black">
                Other payment options
              </div>
              {imageBase64 && (
                <button
                  type="button"
                  data-testid="rescan-payments-btn"
                  onClick={runPaymentOCR}
                  disabled={ocringPayments || ocring}
                  className="border-2 border-black bg-white text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:bg-black hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {ocringPayments ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Re-scan payment details
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="BPAY Biller Code" testid="field-bpay-biller">
                <Input
                  data-testid="input-bpay-biller"
                  className="swiss-input mono"
                  value={form.bpay_biller_code}
                  onChange={update("bpay_biller_code")}
                  placeholder="e.g. 12345"
                />
              </Field>
              <Field label="BPAY Reference (CRN)" testid="field-bpay-ref">
                <Input
                  data-testid="input-bpay-ref"
                  className="swiss-input mono"
                  value={form.bpay_reference}
                  onChange={update("bpay_reference")}
                  placeholder="e.g. 9876543210"
                />
              </Field>
              <Field label="PayID" testid="field-payid">
                <Input
                  data-testid="input-payid"
                  className="swiss-input"
                  value={form.payid}
                  onChange={update("payid")}
                  placeholder="email, mobile or ABN"
                />
              </Field>
              <Field label="Online Payment Link" testid="field-payment-url">
                <Input
                  data-testid="input-payment-url"
                  className="swiss-input"
                  value={form.payment_url}
                  onChange={update("payment_url")}
                  placeholder="Stripe / PayPal / Square URL"
                />
              </Field>
              <Field label="Other Accepted Methods" testid="field-accepted-methods">
                <Input
                  data-testid="input-accepted-methods"
                  className="swiss-input"
                  value={form.accepted_methods}
                  onChange={update("accepted_methods")}
                  placeholder="Visa, Mastercard, Amex, Cheque, Cash"
                />
              </Field>
            </div>
          </div>

          <div className="pt-6">
            <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-black border-b-2 border-black pb-2 mb-4">
              Schedule
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
              {[
                { key: "single", label: "One-time", hint: "A single bill" },
                { key: "recurring", label: "Recurring", hint: "Rent, wages, subscriptions" },
                { key: "plan", label: "Payment Plan", hint: "Split total into installments" },
              ].map((opt) => {
                const active = form.bill_type === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    data-testid={`bill-type-${opt.key}`}
                    onClick={() =>
                      setForm((f) => ({ ...f, bill_type: opt.key }))
                    }
                    className={`text-left p-4 border-2 transition-colors ${
                      active
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    <div className="font-bold uppercase tracking-widest text-xs">
                      {opt.label}
                    </div>
                    <div
                      className={`text-[11px] mt-1 ${active ? "text-neutral-300" : "text-neutral-500"}`}
                    >
                      {opt.hint}
                    </div>
                  </button>
                );
              })}
            </div>

            {form.bill_type !== "single" && (
              <div className="grid grid-cols-2 gap-5" data-testid="schedule-fields">
                <Field label="Frequency" testid="field-frequency">
                  <select
                    data-testid="input-frequency"
                    className="swiss-input w-full appearance-none pr-6 cursor-pointer"
                    value={form.frequency}
                    onChange={update("frequency")}
                  >
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </Field>
                <Field
                  label={
                    form.bill_type === "plan"
                      ? "Number of Installments"
                      : "Number of Occurrences"
                  }
                  testid="field-occurrences"
                >
                  <Input
                    data-testid="input-occurrences"
                    className="swiss-input mono"
                    value={form.occurrences}
                    onChange={update("occurrences")}
                    inputMode="numeric"
                    placeholder="12"
                  />
                </Field>
                {form.bill_type === "plan" && form.amount_due && form.occurrences && (
                  <div className="col-span-2 text-xs text-neutral-600">
                    Each installment ≈{" "}
                    <span className="font-bold mono">
                      {(
                        Number(form.amount_due) / Math.max(Number(form.occurrences) || 1, 1)
                      ).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })}
                    </span>
                    . Invoice numbers will be suffixed with -01, -02, …
                  </div>
                )}
                {form.bill_type === "recurring" && (
                  <div className="col-span-2 text-xs text-neutral-600">
                    Each bill will be <span className="font-bold mono">
                      {form.amount_due
                        ? Number(form.amount_due).toLocaleString("en-AU", {
                            style: "currency",
                            currency: "AUD",
                          })
                        : "—"}
                    </span>
                    . Invoice numbers will be suffixed with -01, -02, …
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pt-4 flex flex-wrap gap-3 items-center">
            <button
              data-testid="save-bill-btn"
              type="submit"
              disabled={saving || ocring}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              Save Bill
            </button>
            <button
              data-testid="cancel-btn"
              type="button"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaptureBill;
